'use client';

import EthereumFormatAPRString from '#/components/EthereumFormatAPRString';
import EthereumFormatUnitsString from '#/components/EthereumFormatUnitsString';
import { Skeleton } from '#/components/ui/skeleton';
import { useTranslation } from '#/contexts/useTranslation';
import useCoinGeckoPriceSWR from '#/data/coingecko/useCoinGeckoPriceSWR';
import useEthereumAPRSummary from '#/data/v2/useEthereumAPRSummary';
import useEthereumBalanceSummary from '#/data/v2/useEthereumBalanceSummary';
import useEthereumRewardsSummary from '#/data/v2/useEthereumRewardsSummary';
import { Translation } from 'intl-lib';
import { useCallback } from 'react';

export default function Page() {
  const translation = useTranslation(
    translation => translation.my_staking.Cards
  );
  const { data: balanceData, isLoading: isBalanceLoading } =
    useEthereumBalanceSummary();
  const { data: rewardsData, isLoading: isRewardsLoading } =
    useEthereumRewardsSummary();
  const { data: aprData, isLoading: isAprLoading } = useEthereumAPRSummary();
  const { data: priceData } = useCoinGeckoPriceSWR();
  const ethToUsd = useCallback(
    (value: string | undefined) => {
      if (value != null && priceData?.data?.data?.ethereum?.usd != null) {
        return (
          Number(value) * priceData.data.data.ethereum.usd
        ).toLocaleString('en-US', {
          maximumFractionDigits: 2,
        });
      }
      return '-';
    },
    [priceData]
  );

  return (
    <div className="grid grid-cols-2 @5xl:grid-cols-4 gap-3">
      <dl className="flex flex-col items-start gap-4 bg-white p-5 rounded-2xl">
        <dt className="text-sm text-gray-500 whitespace-nowrap">
          {translation.TotalActiveValidators}
        </dt>
        <dd>
          {isBalanceLoading ? (
            <Skeleton className="h-auto w-60 inline-block align-middle" />
          ) : (
            <span className="text-xl font-bold">
              {balanceData?.data.data?.validators}
            </span>
          )}
        </dd>
      </dl>
      <dl className="flex flex-col items-start gap-4 bg-white p-5 rounded-2xl">
        <dt className="text-sm text-gray-500 whitespace-nowrap">
          {translation.TotalStaked}
        </dt>
        <div className="flex flex-col gap-1">
          <dd>
            {isRewardsLoading ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <span className="text-xl font-bold">
                <EthereumFormatUnitsString
                  value={balanceData?.data.data?.effectiveBalance}
                />
              </span>
            )}
          </dd>
          <dd className="text-gray-500 whitespace-nowrap">
            ~ ${ethToUsd(balanceData?.data.data?.effectiveBalance)}
          </dd>
        </div>
      </dl>
      <dl className="flex flex-col items-start gap-4 bg-white p-5 rounded-2xl">
        <dt className="text-sm text-gray-500 whitespace-nowrap">
          <Translation path="rewards.overview.TotalRewards" />
        </dt>
        <div className="flex flex-col gap-1">
          <dd>
            {isRewardsLoading ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <span className="text-xl font-bold">
                <EthereumFormatUnitsString
                  value={rewardsData?.data.data?.totalRewards}
                />
              </span>
            )}
          </dd>
          <dd className="text-gray-500 whitespace-nowrap">
            ~ ${ethToUsd(rewardsData?.data.data?.totalRewards)}
          </dd>
        </div>
      </dl>
      <dl className="flex flex-col items-start gap-4 bg-white p-5 rounded-2xl">
        <dt className="text-sm text-gray-500 whitespace-nowrap">
          {translation.APR}
        </dt>
        <dd>
          {isAprLoading ? (
            <Skeleton className="h-8 w-40" />
          ) : (
            <div className="text-xl font-bold truncate flex gap-1 items-center">
              <EthereumFormatAPRString value={aprData?.data.data?.aprTotal} />
            </div>
          )}
        </dd>
      </dl>
    </div>
  );
}
